import React, { useCallback, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { useClientCountry } from '../../hooks/useClientCountry';
import { resolveStoreProductImage } from '../../services/appContentImageFallback';
import type { PublicProduct } from '../marketplace/types';
import { ServiceCard, type Service } from '../marketplace/components/ServiceCard';
import {
  filterByType,
  getProductTypeLabel,
  getCourseCount,
  getServiceCount,
  type StoreFilter,
} from './storeTypes';
import './StorePage.css';
/* ── Map API product → ServiceCard shape ── */
function toService(product: PublicProduct): Service {
  const image = resolveStoreProductImage(product.slug, product.image_url);
  const label = getProductTypeLabel(product.product_type);
  let price = product.total_amount ?? product.price ?? 0;
  if (product.product_type === 'scheduled_seats' && product.offerings && product.offerings.length > 0) {
    const amounts = product.offerings
      .map(o => ('total_amount' in o ? o.total_amount : undefined) ?? o.price ?? 0)
      .filter(a => a > 0);
    if (amounts.length > 0) price = Math.min(...amounts);
  }

  return {
    slug: product.slug,
    title: product.name,
    category: label === 'COURSE' ? 'Course' : 'Service',
    badgeType: label === 'COURSE' ? 'new' : undefined,
    badge: label === 'COURSE' ? 'Course' : undefined,
    price: `${product.currency} $${price.toFixed(2)}`,
    image,
    placeholderColor: label === 'COURSE' ? '#1a1a2e' : '#1a2e1a',
    shortDesc: product.description,
    fullDesc: product.description,
    highlights: [
      label === 'COURSE'
        ? 'Certified instructor-led program'
        : 'Professional service by CWY experts',
      'Backed by CWY quality guarantee',
      'Instant access after purchase',
    ],
  };
}

/* ── Skeleton card ── */
const SkeletonCard: React.FC = () => (
  <div className="ccard ccard--skeleton">
    <div className="svc-skeleton-thumb" />
    <div className="ccard-body">
      <div className="svc-skeleton-line svc-skeleton-line--short" />
      <div className="svc-skeleton-line" />
      <div className="svc-skeleton-line svc-skeleton-line--mid" />
    </div>
    <div className="ccard-foot">
      <div className="svc-skeleton-line svc-skeleton-line--price" />
    </div>
  </div>
);

/* ── Filter pill button ── */
const FilterPill: React.FC<{
  label: string;
  icon: string;
  active: boolean;
  onClick: () => void;
}> = ({ label, icon, active, onClick }) => (
  <button
    className={`store-filter-pill${active ? ' store-filter-pill--active' : ''}`}
    onClick={onClick}
  >
    <i className={`ti ${icon}`} style={{ fontSize: 14 }} />
    {label}
  </button>
);

export const StorePage: React.FC = () => {
  const navigate = useNavigate();
  const { country } = useClientCountry();
  const [products, setProducts] = useState<PublicProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<StoreFilter>('all');

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const url = new URL(API_ENDPOINTS.PRODUCTS_PUBLIC);
    url.searchParams.set('country', country);
    fetch(url.toString())
      .then((r) => r.json())
      .then((data) => setProducts(data.products ?? []))
      .catch(() => setError('Unable to load products. Please try again.'))
      .finally(() => setLoading(false));
  }, [country]);

  useEffect(() => { load(); }, [load]);

  const filtered = filterByType(products, filter);
  const courseCount = getCourseCount(products);
  const serviceCount = getServiceCount(products);
  const services = filtered.map(toService);

  return (
    <div className="store-page">
      {/* ── Page header ── */}
      <motion.div
        className="saas-sec-head"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="saas-sec-head-left">
          <span className="saas-sec-eyebrow">
            <span className="saas-sec-eyebrow-line" />
            CWY Store
          </span>
          <h1 className="saas-sec-title">Browse all products &amp; services</h1>
          <p className="saas-sec-sub">
            Certified courses and professional services to accelerate your Canadian journey.
          </p>
        </div>
      </motion.div>

      {/* ── Filter pills ── */}
      <motion.div
        className="store-filter-row"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
      >
        <FilterPill
          label={`All (${products.length})`}
          icon="ti-layout-grid"
          active={filter === 'all'}
          onClick={() => setFilter('all')}
        />
        <FilterPill
          label={`Courses (${courseCount})`}
          icon="ti-school"
          active={filter === 'course'}
          onClick={() => setFilter('course')}
        />
        <FilterPill
          label={`Services (${serviceCount})`}
          icon="ti-tool"
          active={filter === 'service'}
          onClick={() => setFilter('service')}
        />
      </motion.div>

      {/* ── Error banner ── */}
      {error && (
        <div className="store-error-banner">
          <i className="ti ti-alert-circle" style={{ fontSize: 16 }} />
          {error}
          <button className="store-error-retry" onClick={load}>Retry</button>
        </div>
      )}

      <button type="button" className="store-esim-card" onClick={() => navigate('/store/canada-esim')}>
        Canada eSIM — pay in INR before you fly
      </button>

      {/* ── Grid ── */}
      <div className="catalog-grid">
        {loading
          ? [1, 2, 3, 4].map((k) => (
              <div key={k} className="catalog-grid-item">
                <SkeletonCard />
              </div>
            ))
          : services.map((svc, i) => (
              <div key={svc.slug} className="catalog-grid-item">
                <ServiceCard service={svc} index={i} />
              </div>
            ))}
      </div>

      {/* ── Empty state ── */}
      {!loading && filtered.length === 0 && !error && (
        <motion.div
          className="store-empty"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.35 }}
        >
          <i className="ti ti-package-off" style={{ fontSize: 40, color: '#ccc' }} />
          <p className="store-empty-text" style={{ fontWeight: 600, fontSize: '1.05rem', color: '#0f1111' }}>
            {products.length === 0
              ? 'New products in the store will be added soon — stay tuned!'
              : `No ${filter === 'all' ? 'products' : filter === 'course' ? 'courses' : 'services'} in this filter right now.`}
          </p>
          {products.length === 0 && (
            <p className="store-empty-text" style={{ marginTop: 8 }}>
              We&apos;re preparing courses and services for your region. Check back shortly.
            </p>
          )}
          <button className="store-empty-btn" onClick={() => navigate('/')}>Back to home</button>
        </motion.div>
      )}
    </div>
  );
};
